import { useCallback, useEffect, useState } from 'react';
import { getFullKey, getObjectFromStorage, replaceObjectInStorage } from '../services/LocalStorage';

export function getLocalStorageUpdateEventType(storageKey: string) {
    return `local-storage-update-${getFullKey(storageKey)}`;
}

/**
 * This can be used instead of useState to store information in localStorage. The state is shared between all uses
 * of the hook with the same storageKey, even across tabs. This means that setting the value in one component causes a
 * re-render of all other components using the hook, with the new value. Even when they're in other browser tabs.
 *
 * @param storageKey This identifies the storage object within SI. It's prefixed properly behind the scenes, so pick a short name
 * @param init If there is nothing stored already, use this as the default value (similar to how useState works)
 */
export default function useLocalStorage<T>(storageKey: string, init: T): [T, (o: T) => void] {
    const eventType = getLocalStorageUpdateEventType(storageKey);

    const [state, setState] = useState(() => getObjectFromStorage<T>(storageKey, init));

    const replace = useCallback(
        (o: T) => {
            replaceObjectInStorage(storageKey, o);
            const objectFromStorage = getObjectFromStorage<T>(storageKey, init);
            window.dispatchEvent(new CustomEvent(eventType, { detail: objectFromStorage }));
        },
        [init, storageKey, eventType]
    );

    useEffect(() => {
        const handleStorageEvent = (event: StorageEvent) => {
            if (event.key === getFullKey(storageKey)) {
                const objectFromStorage = getObjectFromStorage<T>(storageKey, init);
                window.dispatchEvent(new CustomEvent(eventType, { detail: objectFromStorage }));
            }
        };

        window.addEventListener('storage', handleStorageEvent);
        return () => window.removeEventListener('storage', handleStorageEvent);
    }, [init, storageKey, eventType]);

    useEffect(() => {
        const handleStateUpdateEvent = (event: CustomEventInit) => {
            setState(event.detail);
        };

        window.addEventListener(eventType, handleStateUpdateEvent);
        return () => window.removeEventListener(eventType, handleStateUpdateEvent);
    }, [eventType]);

    return [state, replace];
}
